import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { EmailBlock, EmailBlockType } from './types';
import { emailBuilderBlockBackground } from './blockStyles';
import { InnerDropZone } from './DropZone';
import { RowActions } from './RowActions';

export const ContainerBlock: React.FC<{
  block: EmailBlock;
  isSelected: boolean;
  isDragging: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<EmailBlock>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  renderChild: (child: EmailBlock, idx: number) => React.ReactNode;
  insertAt: (index: number, data: any) => void;
}> = ({ block, isSelected, isDragging, onSelect, onDelete, onDuplicate, onDragStart, onDragEnd, renderChild, insertAt }) => {
  const common = {
    px: 2,
    py: 1.5,
    border: '1px solid',
    borderRadius: 2,
    mx: 1,
    borderColor: isSelected ? 'primary.main' : 'divider',
    backgroundColor: emailBuilderBlockBackground,
    cursor: 'move',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: isSelected ? 'primary.main' : 'primary.light'
    }
  } as const;
  const children = block.children || [];
  return (
    <Box sx={{ ...common, borderStyle: 'dashed' }}>
      <Box 
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5, cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <Typography variant="subtitle2" sx={{ mb: 0, wordBreak: 'break-word' }}>
          Container : {block.title || 'Container'}
        </Typography>
        <RowActions onDelete={onDelete} onDuplicate={onDuplicate} />
      </Box>
      <InnerDropZone isDragging={isDragging} onDrop={(d) => insertAt(0, d)} onDropComplete={onDragEnd} />
      {children.map((child, idx) => (
        <React.Fragment key={child.id}>
          {renderChild(child, idx)}
          <InnerDropZone isDragging={isDragging} onDrop={(d) => insertAt(idx + 1, d)} onDropComplete={onDragEnd} />
        </React.Fragment>
      ))}
    </Box>
  );
};

export const ContainerBlockDialog: React.FC<{
  open: boolean;
  draft: EmailBlock;
  setDraft: (next: EmailBlock) => void;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, draft, setDraft, onCancel, onConfirm }) => {
  if (draft.type !== 'container') return null;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Container</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            value={draft.title || ''}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            fullWidth
            size="small"
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Padding Top (px)"
              type="number"
              value={draft.paddingTop ?? 16}
              onChange={(e) => setDraft({ ...draft, paddingTop: Number(e.target.value) })}
              fullWidth
              size="small"
              inputProps={{ min: 0, max: 100 }}
            />
            <TextField
              label="Padding Right (px)"
              type="number"
              value={draft.paddingRight ?? 16}
              onChange={(e) => setDraft({ ...draft, paddingRight: Number(e.target.value) })}
              fullWidth
              size="small"
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Padding Bottom (px)"
              type="number"
              value={draft.paddingBottom ?? 16}
              onChange={(e) => setDraft({ ...draft, paddingBottom: Number(e.target.value) })}
              fullWidth
              size="small"
              inputProps={{ min: 0, max: 100 }}
            />
            <TextField
              label="Padding Left (px)"
              type="number"
              value={draft.paddingLeft ?? 16}
              onChange={(e) => setDraft({ ...draft, paddingLeft: Number(e.target.value) })}
              fullWidth
              size="small"
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>

          <TextField
            label="Background Color"
            type="color"
            value={draft.background || '#ffffff'}
            onChange={(e) => setDraft({ ...draft, background: e.target.value })}
            fullWidth
            size="small"
            InputProps={{
              style: { height: '40px' }
            }}
          />

          {/* Container Preview */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>Preview:</Typography>
            <Box
              sx={{
                paddingTop: `${draft.paddingTop ?? 16}px`,
                paddingRight: `${draft.paddingRight ?? 16}px`,
                paddingBottom: `${draft.paddingBottom ?? 16}px`,
                paddingLeft: `${draft.paddingLeft ?? 16}px`,
                backgroundColor: draft.background || '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Container Content
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancel}>Cancel</Button>
        <Button color="success" variant="contained" onClick={onConfirm}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

// Container block instantiation function
export const instantiateContainerBlock = (): EmailBlock => {
  return {
    id: '',
    type: 'container',
    title: 'Container',
    paddingTop: 16,
    paddingRight: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    background: '#ffffff',
    children: []
  };
};

// Container block HTML rendering function
export const renderContainerBlockToHtml = (block: EmailBlock, renderChild: (child: EmailBlock) => string): string => {
  const paddingTop = block.paddingTop ?? 16;
  const paddingRight = block.paddingRight ?? 16;
  const paddingBottom = block.paddingBottom ?? 16;
  const paddingLeft = block.paddingLeft ?? 16;
  const bg = block.background || '#ffffff';
  
  // Process each child and extract content with styles preserved
  let innerContent = '';
  (block.children || []).forEach(child => {
    const childHtml = renderChild(child);
    // Extract both the style and content from the <tr><td>...</td></tr> structure
    const styleMatch = childHtml.match(/<tr><td style="([^"]*)">(.*?)<\/td><\/tr>/s);
    if (styleMatch) {
      const style = styleMatch[1];
      const content = styleMatch[2];
      // Wrap content in a div that preserves the child's styles
      innerContent += `<div style="${style}">${content}</div>`;
    } else {
      // Fallback: extract content without style
      const contentMatch = childHtml.match(/<tr><td[^>]*>(.*?)<\/td><\/tr>/s);
      if (contentMatch) {
        innerContent += contentMatch[1];
      } else {
        innerContent += childHtml;
      }
    }
  });
  
  // Wrap all children in a single padded container
  const wrappedChildren = innerContent ? `<tr><td style="padding:${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px;">${innerContent}</td></tr>` : '';
  
  return `<tr><td><table role="presentation" width="100%" cellPadding="0" cellSpacing="0" style="background:${bg}; border:1px solid #e5e7eb; border-radius:8px;"><tbody>${wrappedChildren}</tbody></table></td></tr>`;
};


